// Generated by Dev AI Agent.
import {Injectable} from '@angular/core';
import {
  IntlTelInputFactory,
  IntlTelInputInitOptions,
  IntlTelInputInstance
} from '../models/intl-tel-input';
import {ensureZoneSignalCompat} from './zone-signal-compat';

const DEFAULT_COUNTRY_ISO2 = 'us';
// intl-tel-input format enum: 0 === E.164 (e.g. "+14155552671").
const E164_FORMAT = 0;

declare const window: Window & { intlTelInput?: IntlTelInputFactory };

/**
 * Wraps the country-aware intl-tel-input widget for the leadgen phone input.
 *
 * The widget defaults to United States and never performs IP-based country
 * detection (geoIpLookup disabled). `separateDialCode` shows the selected
 * country's dial code as a fixed label beside the flag (tied to the country
 * selector, not editable) rather than as text in the input, so the input holds
 * only the national number. `strictMode` blocks non-dialling characters as the
 * user types and caps the length per country; `isValidNumber()` (libphonenumber)
 * is the source of truth for validity. Advisory only — it constrains typing, not
 * submission (BR-011).
 */
@Injectable()
export class PhoneWidgetService {
  private instance: IntlTelInputInstance | null = null;
  private inputElement: HTMLInputElement | null = null;
  private countryChangeListener: (() => void) | null = null;

  public initialize(input: HTMLInputElement): void {
    if (!this.canInitialize(input)) {
      return;
    }
    this.destroy();
    ensureZoneSignalCompat();
    this.inputElement = input;
    this.instance = window.intlTelInput(input, this.buildOptions());
    this.countryChangeListener = () => this.clearNumber();
    input.addEventListener('countrychange', this.countryChangeListener);
  }

  public isValid(): boolean {
    if (!this.instance) {
      return false;
    }
    return this.instance.isValidNumber() === true;
  }

  public getCleanedNumber(): string {
    if (!this.instance) {
      return '';
    }
    return this.formatWithDialCodeSeparator(this.instance.getNumber(E164_FORMAT) || '');
  }

  private formatWithDialCodeSeparator(e164Number: string): string {
    if (!this.instance) {
      return e164Number;
    }
    const dialCode = this.instance.getSelectedCountryData().dialCode;
    if (!dialCode || e164Number.indexOf('+' + dialCode) !== 0) {
      return e164Number;
    }
    return '+' + dialCode + '-' + e164Number.slice(dialCode.length + 1);
  }

  public hasValue(): boolean {
    return !!(this.inputElement && this.inputElement.value && this.inputElement.value.trim());
  }

  public destroy(): void {
    if (this.inputElement && this.countryChangeListener) {
      this.inputElement.removeEventListener('countrychange', this.countryChangeListener);
    }
    if (this.instance) {
      this.instance.destroy();
    }
    this.instance = null;
    this.inputElement = null;
    this.countryChangeListener = null;
  }

  private canInitialize(input: HTMLInputElement): boolean {
    return !!input && typeof window.intlTelInput === 'function';
  }

  private clearNumber(): void {
    if (!this.inputElement || !this.inputElement.value) {
      return;
    }
    this.inputElement.value = '';
    this.inputElement.dispatchEvent(new Event('input', { bubbles: true }));
  }

  private buildOptions(): IntlTelInputInitOptions {
    return {
      initialCountry: DEFAULT_COUNTRY_ISO2,
      geoIpLookup: null,
      separateDialCode: true,
      autoPlaceholder: 'off',
      strictMode: true,
      useFullscreenPopup: false
    };
  }
}
